/**
 * TaskCreateForm Component
 *
 * Form for creating new tasks within a project context.
 * Uses React Hook Form + Zod for form state and validation.
 * Integrates with DrawerStackContext for unsaved changes protection.
 *
 * @see docs/stories/DRAWER-2.2-implement-create-task-form.md
 */

import React, { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FormField } from "@/components/ui/form-field";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { taskService, type Task, type CreateTaskInput } from "@/services/taskService";
import { supabase } from "@/lib/supabaseClient";

// ============================================================================
// Schema
// ============================================================================

const taskCreateSchema = z.object({
  title: z
    .string()
    .min(1, "Title is required")
    .max(200, "Title must be less than 200 characters"),
  description: z.string().optional(),
  due_date: z.date().nullable().optional(),
  priority: z.enum(["low", "medium", "high", "urgent"]),
  assignee_id: z.string().nullable().optional(),
});

type TaskCreateFormValues = z.infer<typeof taskCreateSchema>;

// ============================================================================
// Types
// ============================================================================

export interface TaskCreateFormProps {
  /** Project ID to associate the task with */
  projectId: string;
  /** Callback when task is successfully created */
  onSuccess?: (task: Task) => void;
  /** Callback when loading state changes */
  onLoadingChange?: (isLoading: boolean) => void;
  /** Callback when form dirty state changes */
  onDirtyChange?: (isDirty: boolean) => void;
}

interface UserOption {
  id: string;
  name: string;
  email: string;
}

// ============================================================================
// Component
// ============================================================================

export function TaskCreateForm({
  projectId,
  onSuccess,
  onLoadingChange,
  onDirtyChange,
}: TaskCreateFormProps) {
  // =========================================================================
  // Form Setup
  // =========================================================================

  const form = useForm<TaskCreateFormValues>({
    resolver: zodResolver(taskCreateSchema),
    defaultValues: {
      title: "",
      description: "",
      due_date: null,
      priority: "medium",
      assignee_id: null,
    },
    mode: "onChange",
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty, isSubmitting },
    reset,
  } = form;

  // =========================================================================
  // User Options State
  // =========================================================================

  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Watch form values for controlled components
  const priority = watch("priority");
  const assigneeId = watch("assignee_id");
  const dueDate = watch("due_date");

  // =========================================================================
  // Effects
  // =========================================================================

  // Report dirty state changes
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Report loading state changes
  useEffect(() => {
    onLoadingChange?.(isSubmitting);
  }, [isSubmitting, onLoadingChange]);

  // Load current user and set as default assignee
  useEffect(() => {
    async function loadCurrentUser() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setCurrentUserId(user.id);
          setValue("assignee_id", user.id);

          // Get user's name for display
          const { data: profile } = await supabase
            .from("users")
            .select("first_name, last_name")
            .eq("id", user.id)
            .single();

          const displayName = profile
            ? `Myself (${profile.first_name || ""} ${profile.last_name || ""}`.trim() + ")"
            : "Myself";

          setUserOptions([
            { id: user.id, name: displayName, email: user.email || "" },
          ]);
        }
      } catch (error) {
        console.error("Error loading current user:", error);
      }
    }
    loadCurrentUser();
  }, [setValue]);

  // =========================================================================
  // User Loading
  // =========================================================================

  const loadUsers = useCallback(async () => {
    if (usersLoaded || usersLoading) return;

    setUsersLoading(true);
    try {
      const { data: users, error } = await supabase
        .from("users")
        .select("id, first_name, last_name, email")
        .is("deleted_at", null)
        .eq("is_active", true)
        .order("first_name");

      if (error) throw error;

      const options: UserOption[] = (users || []).map((user) => {
        const name = `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email;
        const isSelf = user.id === currentUserId;
        return {
          id: user.id,
          name: isSelf ? `Myself (${name})` : name,
          email: user.email,
        };
      });

      // Sort so "Myself" is always first
      options.sort((a, b) => {
        if (a.id === currentUserId) return -1;
        if (b.id === currentUserId) return 1;
        return a.name.localeCompare(b.name);
      });

      setUserOptions(options);
      setUsersLoaded(true);
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  }, [usersLoaded, usersLoading, currentUserId]);

  // =========================================================================
  // Submit Handler
  // =========================================================================

  const onSubmit = async (data: TaskCreateFormValues) => {
    try {
      const taskInput: CreateTaskInput = {
        title: data.title.trim(),
        description: data.description?.trim() || undefined,
        priority: data.priority,
        due_date: data.due_date?.toISOString(),
        assignee_id: data.assignee_id || undefined,
        project_id: projectId,
      };

      const task = await taskService.createTask(taskInput);

      toast.success("Task created", {
        description: `"${task.title}" has been created.`,
      });

      // Reset form (clears dirty state)
      reset();

      // Call success callback
      onSuccess?.(task);
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error("Failed to create task", {
        description: (error as Error).message,
      });
    }
  };

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <form
      id="task-create-form"
      onSubmit={handleSubmit(onSubmit)}
      className="p-6 space-y-6"
    >
      {/* Title */}
      <FormField
        label="Title"
        htmlFor="task-title"
        required
        error={errors.title?.message}
      >
        <Input
          id="task-title"
          placeholder="Enter task title"
          {...register("title")}
        />
      </FormField>

      {/* Description */}
      <FormField
        label="Description"
        htmlFor="task-description"
        error={errors.description?.message}
      >
        <Textarea
          id="task-description"
          placeholder="Add task description (optional)"
          rows={4}
          {...register("description")}
        />
      </FormField>

      {/* Two-column layout for Priority and Due Date */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Priority */}
        <FormField
          label="Priority"
          htmlFor="task-priority"
          required
          error={errors.priority?.message}
        >
          <Select
            value={priority}
            onValueChange={(value) =>
              setValue("priority", value as TaskCreateFormValues["priority"], {
                shouldDirty: true,
              })
            }
          >
            <SelectTrigger id="task-priority">
              <SelectValue placeholder="Select priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        {/* Due Date */}
        <FormField
          label="Due Date"
          htmlFor="task-due-date"
          error={errors.due_date?.message}
        >
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                id="task-due-date"
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !dueDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dueDate ? format(dueDate, "PPP") : "Set due date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={dueDate ?? undefined}
                onSelect={(date) => {
                  setValue("due_date", date ?? null, { shouldDirty: true });
                  setCalendarOpen(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </FormField>
      </div>

      {/* Assignee */}
      <FormField
        label="Assignee"
        htmlFor="task-assignee"
        error={errors.assignee_id?.message}
      >
        {usersLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <SearchableSelect
            id="task-assignee"
            value={assigneeId || ""}
            onValueChange={(value) =>
              setValue("assignee_id", value || null, { shouldDirty: true })
            }
            options={userOptions.map((u) => ({
              label: u.name.startsWith("Myself")
                ? u.name
                : `${u.name} (${u.email})`,
              value: u.id,
            }))}
            placeholder="Select assignee..."
            onOpenChange={(open) => {
              if (open) loadUsers();
            }}
          />
        )}
      </FormField>
    </form>
  );
}
